/** Visual viewport adaptation only: opening a keyboard never edits the camera or scene. */
export function installNativeViewport(root: HTMLElement) {
  const viewport = window.visualViewport;
  let frame = 0;
  let previousHeight = viewport?.height ?? innerHeight;
  let textFocusHeight = 0;
  let previousWidth = innerWidth;
  const dock = root.querySelector<HTMLElement>(".native-bottom-controls");
  const update = () => {
    frame = 0;
    const narrow = matchMedia("(max-width:1100px)").matches;
    const height = viewport?.height ?? innerHeight;
    const top = viewport?.offsetTop ?? 0;
    // Pinch zoom is a browser gesture, not evidence of an on-screen keyboard.
    const inset =
      narrow && (viewport?.scale ?? 1) === 1
        ? Math.max(0, innerHeight - height - top)
        : 0;
    root.style.setProperty("--native-keyboard-bottom", inset + "px");
    root.style.setProperty("--native-visible-height", height + "px");
    if (Math.abs(innerWidth - previousWidth) > 80) textFocusHeight = 0;
    previousWidth = innerWidth;
    const textFocused = document.activeElement?.id === "native-text-input";
    root.dataset.textInput = String(narrow && textFocused);
    const textKeyboard =
      narrow &&
      (viewport?.scale ?? 1) === 1 &&
      textFocused &&
      Math.max(innerHeight, textFocusHeight) - height > 100;
    root.dataset.textKeyboard = String(textKeyboard);
    if (dock) dock.inert = textKeyboard;
    previousHeight = height;
    const controls = [
      ...root.querySelectorAll<HTMLElement>(
        "#native-context-properties button",
      ),
    ].filter(
      (el) =>
        el.getClientRects().length &&
        !el.closest(".native-popup,.native-property-popup"),
    );
    // Side commands can share the horizontal extent of a wrapped property row.
    // Lift only colliding flyouts, preserving Undo/grid anchors at the toolbar.
    for (const floating of root.querySelectorAll<HTMLElement>(
      "#native-redo,#native-grid-settings",
    )) {
      if (!floating.getClientRects().length) continue;
      const zoom =
        floating.getBoundingClientRect().width / floating.offsetWidth || 1;
      const previous = Number(floating.dataset.floatingLift || 0) * zoom;
      const r = floating.getBoundingClientRect();
      const boxes = controls
        .map((el) => el.getBoundingClientRect())
        .filter((b) => b.right > r.left && b.left < r.right);
      const overlaps = boxes.some(
        (b) => b.bottom > r.top + previous && b.top < r.bottom + previous,
      );
      const lift = overlaps
        ? Math.max(
            0,
            r.bottom + previous - Math.min(...boxes.map((b) => b.top)) + 6,
          ) / zoom
        : 0;
      floating.dataset.floatingLift = String(lift);
      floating.style.setProperty("--native-floating-lift", lift + "px");
    }
    for (const popup of root.querySelectorAll<HTMLElement>(
      ".native-popup, .native-property-popup, .dropdown-content, #native-shape-menu",
    )) {
      if (!popup.getClientRects().length) continue;
      const previous = Number(popup.dataset.viewportShift || 0);
      const zoom = popup.offsetWidth
        ? popup.getBoundingClientRect().width / popup.offsetWidth
        : 1;
      const r = popup.getBoundingClientRect();
      // Contextual options belong above every basic property row, even when the
      // phone layout wraps those rows. Keep DOM ownership/focus with its control.
      const dock = popup.closest<HTMLElement>(".native-bottom-controls");
      const contextual = popup.matches(".native-popup, .native-property-popup");
      const previousLift = Number(popup.dataset.viewportLift || 0);
      const lift =
        dock && contextual
          ? Math.max(
              0,
              (r.bottom +
                previousLift * zoom -
                dock.getBoundingClientRect().top) /
                zoom +
                9,
            )
          : 0;
      popup.dataset.viewportLift = String(lift);
      popup.style.setProperty("--native-popup-lift", lift + "px");
      const left = r.left - previous * zoom,
        right = r.right - previous * zoom;
      const shift = Math.min(0, innerWidth - 8 - right) + Math.max(0, 8 - left);
      popup.dataset.viewportShift = String(shift / zoom);
      popup.style.setProperty("--native-popup-shift", shift / zoom + "px");
    }
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };
  viewport?.addEventListener("resize", schedule);
  viewport?.addEventListener("scroll", schedule);
  window.addEventListener("resize", schedule);
  root.addEventListener("focusin", (e) => {
    if ((e.target as HTMLElement).id === "native-text-input")
      textFocusHeight = Math.max(previousHeight, innerHeight);
    schedule();
  });
  root.addEventListener("focusout", (e) => {
    if ((e.target as HTMLElement).id === "native-text-input")
      textFocusHeight = 0;
    schedule();
  });
  if (dock) new ResizeObserver(schedule).observe(dock);
  // Opening an existing menu or changing its contents also needs edge clamping.
  new MutationObserver(schedule).observe(root, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["hidden", "open", "class", "data-large-icons"],
  });
  update();
}
